/**
 * Business Health (BH) bands from public-data briefings.
 * Strong ≥ strong_above · Stable stable_min–(strong_above-1) · Concern concern_min–(stable_min-1).
 * Defaults align with product thresholds (v3); pass vendor_config thresholds to override.
 */
import {
  DEFAULT_BUSINESS_HEALTH_THRESHOLDS,
  normalizeBusinessHealthThresholds,
  type BusinessHealthThresholds,
} from "../HealthThresholds";

export const BH_STRONG = DEFAULT_BUSINESS_HEALTH_THRESHOLDS.strong_above;
export const BH_STABLE = DEFAULT_BUSINESS_HEALTH_THRESHOLDS.stable_min;
export const BH_CONCERN = DEFAULT_BUSINESS_HEALTH_THRESHOLDS.concern_min;

export type BusinessHealthClass = "Strong" | "Stable" | "Concern";
export type BusinessHealthFlag = "R" | "Y" | "G";

/** Match Account Briefing Legend Business Health chips. */
export const BUSINESS_HEALTH_COLORS = {
  concern: "#ef4444", // red-500 — Concern
  stable: "#eab308", // yellow-500 — Stable
  strong: "#22c55e", // green-500 — Strong
} as const;

/** Soft palette for WatchlistSummary score bubbles (outer / inner / text). */
export const BUSINESS_HEALTH_SCORE_PALETTE = {
  strong: { outer: "#dcfce7", inner: "#bbf7d0", score: "#16a34a" },
  stable: { outer: "#fef3c7", inner: "#fde68a", score: "#d97706" },
  concern: { outer: "#fecaca", inner: "#fca5a5", score: "#dc2626" },
} as const;

/** @deprecated Prefer BUSINESS_HEALTH_COLORS; kept for Birds Eye / legend imports. */
export const HEALTH_DOT_COLORS = {
  red: BUSINESS_HEALTH_COLORS.concern,
  yellow: BUSINESS_HEALTH_COLORS.stable,
  green: BUSINESS_HEALTH_COLORS.strong,
} as const;

export function businessHealthClass(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): BusinessHealthClass | null {
  const bands = normalizeBusinessHealthThresholds(thresholds);
  if (score == null || !Number.isFinite(score) || score < bands.concern_min) {
    return null;
  }
  if (score >= bands.strong_above) return "Strong";
  if (score >= bands.stable_min) return "Stable";
  return "Concern";
}

export function businessHealthLabel(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): BusinessHealthClass | null {
  return businessHealthClass(score, thresholds);
}

/** R / Y / G flag for Public Data matrix + digest UI. */
export function businessHealthFlag(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): BusinessHealthFlag | null {
  const band = businessHealthClass(score, thresholds);
  if (band === "Strong") return "G";
  if (band === "Stable") return "Y";
  if (band === "Concern") return "R";
  return null;
}

export function businessHealthColor(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): string | null {
  const band = businessHealthClass(score, thresholds);
  if (band === "Strong") return BUSINESS_HEALTH_COLORS.strong;
  if (band === "Stable") return BUSINESS_HEALTH_COLORS.stable;
  if (band === "Concern") return BUSINESS_HEALTH_COLORS.concern;
  return null;
}

export function businessHealthColorFromFlag(
  flag: BusinessHealthFlag | string | null | undefined
): string {
  switch (flag) {
    case "G":
      return BUSINESS_HEALTH_COLORS.strong;
    case "Y":
      return BUSINESS_HEALTH_COLORS.stable;
    case "R":
      return BUSINESS_HEALTH_COLORS.concern;
    default:
      return "#6b7280";
  }
}

/** Soft score-bubble colors used across WatchlistSummary sections. */
export function businessHealthScorePalette(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): {
  outer: string;
  inner: string;
  score: string;
} {
  const band = businessHealthClass(score, thresholds);
  if (band === "Strong") return BUSINESS_HEALTH_SCORE_PALETTE.strong;
  if (band === "Stable") return BUSINESS_HEALTH_SCORE_PALETTE.stable;
  return BUSINESS_HEALTH_SCORE_PALETTE.concern;
}

/** Outlined bubble helper — color + label for Account Priorities / shared UI. */
export function businessHealthBand(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): { color: string; label: BusinessHealthClass } | null {
  const label = businessHealthClass(score, thresholds);
  const color = businessHealthColor(score, thresholds);
  if (!label || !color) return null;
  return { color, label };
}
